/**
 * Rebuild the settings cache from WatermelonDB.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * The settings cache had exactly two sources: DEFAULT_SETTINGS, and whatever
 * `setSettingCache` had persisted to the local key-value store. Nothing read
 * the `settings` TABLE at startup — `hydrateFromDB` was called from one place,
 * the Tax & GST save handler in SettingsScreen.
 *
 * Signing out clears the key-value store, so on the next sign-in every saved
 * setting silently reverted to its default for the whole session: the A4
 * invoice layout fell back to "standard", the bank block vanished from the
 * invoice, and RECEIPT_CONFIG — the entire Receipt Designer output — was lost
 * back to preset defaults. The rows were still in IndexedDB and on the server
 * the whole time; nothing was reading them.
 *
 * The same gap hits a second device: it syncs the settings rows down, and then
 * ignores them until somebody happens to open Tax & GST and press save.
 *
 * Runs after the active store is known, because settings are scoped by
 * merchant and that is when the merchant is resolvable.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { Setting } from '@/db/models/System';
import Store from '@/db/models/Store';
import {
  useSettingsStore,
  registerSettingPersister,
  STORE_SCOPED_SETTINGS,
} from './useSettingsStore';

/** What resolving a setting needs off a row. */
export interface SettingRowLike {
  storeId: string | null;
  settingKey: string;
  settingValue: string;
  /** Used only to break a tie between two legacy rows for the same key. */
  updatedAt?: Date | number | null;
}

/**
 * Decide one value per key from rows that may be scoped three different ways.
 *
 * ── Why this is not just "apply them in order" ──────────────────────────────
 * The settings table has always had a nullable store_id, and real data uses
 * it: on this database EVERY existing row — TAX_INCLUSIVE, ROUNDING_MODE, all
 * of them — carries a store id, written by an earlier client. Nothing read it.
 * Every row for the merchant was loaded and applied in whatever order the query
 * returned, so a merchant with two shops got whichever row happened to come
 * last, and which one that was could change between loads.
 *
 * That makes precedence explicit instead:
 *
 *   store-scoped key  this store's row  →  the merchant-wide row
 *   everything else   the merchant-wide row  →  this store's row  →  the most
 *                     recently updated row from anywhere under this merchant
 *
 * The last clause is what keeps a shop's existing settings working. Those
 * legacy rows are merchant-wide values that happen to be stored against a
 * store, and dropping them would silently reset a live shop's tax and rounding
 * to defaults. A store-scoped key gets no such fallback: another branch's
 * answer is never this branch's answer.
 */
export function resolveSettingRows(
  rows: SettingRowLike[],
  storeId: string,
): { settingKey: string; settingValue: string }[] {
  const byKey = new Map<string, SettingRowLike[]>();
  for (const row of rows) {
    const list = byKey.get(row.settingKey);
    if (list) list.push(row);
    else byKey.set(row.settingKey, [row]);
  }

  const stamp = (r: SettingRowLike) =>
    r.updatedAt instanceof Date ? r.updatedAt.getTime() : Number(r.updatedAt ?? 0);

  const out: { settingKey: string; settingValue: string }[] = [];
  for (const [key, candidates] of byKey) {
    const mine = candidates.find((r) => r.storeId === storeId);
    const merchantWide = candidates.find((r) => r.storeId == null);

    let chosen: SettingRowLike | undefined;
    if (STORE_SCOPED_SETTINGS.has(key)) {
      chosen = mine ?? merchantWide;
    } else {
      chosen =
        merchantWide ??
        mine ??
        [...candidates].sort((a, b) => stamp(b) - stamp(a))[0];
    }

    if (chosen) out.push({ settingKey: key, settingValue: chosen.settingValue });
  }
  return out;
}

export async function hydrateSettingsFromDatabase(storeId: string | null): Promise<number> {
  if (!storeId) return 0;

  try {
    const store = await database.get<Store>('stores').find(storeId);
    const merchantId = store.merchantId;
    if (!merchantId) return 0;

    // Every row for the merchant, including other stores' — resolveSettingRows
    // needs them to fall back on for the legacy rows described above, and it is
    // the thing that decides which one wins.
    const rows = await database
      .get<Setting>('settings')
      .query(Q.where('merchant_id', merchantId), Q.where('is_deleted', false))
      .fetch();

    const resolved = resolveSettingRows(rows, storeId);
    if (resolved.length === 0) return 0;

    useSettingsStore.getState().hydrateFromDB(resolved);
    return resolved.length;
  } catch (err) {
    console.warn('[settings] could not hydrate from the database', err);
    return 0;
  }
}

/**
 * Make `setSetting` durable for this merchant.
 *
 * Every control on the Billing and Printing settings pages calls `setSetting`,
 * which was a cache write only — nothing reached the database, so nothing
 * synced and nothing survived a restart. Registering this once the merchant is
 * known makes all of those durable without changing a single call site.
 *
 * Writes are fire-and-forget: a settings toggle must not block the UI, and a
 * failure leaves the value live in the cache for this session, which is no
 * worse than the behaviour it replaces.
 */
export async function installSettingsPersister(storeId: string | null): Promise<void> {
  if (!storeId) {
    registerSettingPersister(null);
    return;
  }
  try {
    const store = await database.get<Store>('stores').find(storeId);
    const merchantId = store.merchantId;
    if (!merchantId) {
      registerSettingPersister(null);
      return;
    }
    registerSettingPersister((key, value) => {
      const scopedToStore = STORE_SCOPED_SETTINGS.has(key);
      void database
        .write(async () => {
          const candidates = await database
            .get<Setting>('settings')
            .query(Q.where('merchant_id', merchantId), Q.where('setting_key', key))
            .fetch();

          // Which row to write, mirroring resolveSettingRows exactly — the row
          // that is updated must be the row that will then be READ back.
          //
          // For a merchant-wide key that means updating the legacy row stored
          // against a store rather than adding a null-scoped one beside it:
          // two rows for one key, and the resolver would keep choosing the
          // stale one, so the setting would appear not to save.
          const target = scopedToStore
            ? candidates.find((r) => r.storeId === storeId)
            : candidates.find((r) => r.storeId == null) ??
              candidates.find((r) => r.storeId === storeId) ??
              candidates[0];

          const rowStoreId = scopedToStore ? storeId : target?.storeId ?? null;

          if (target) {
            await target.update((row) => {
              row.settingValue = value;
              row.isSynced = false;
            });
          } else {
            await database.get<Setting>('settings').create((row) => {
              row.merchantId = merchantId;
              row.storeId = rowStoreId;
              row.category = 'billing';
              row.settingKey = key;
              row.settingValue = value;
              row.version = 1;
              row.isSynced = false;
              row.isDeleted = false;
            });
          }
        })
        .catch((err) => console.warn(`[settings] could not persist ${key}`, err));
    });
  } catch (err) {
    console.warn('[settings] could not install the persister', err);
    registerSettingPersister(null);
  }
}
